// upgradeReasons.ts — a fixed vocabulary for WHY an upgrade prompt opened.
//
// Before this, UpgradePrompt reported its trigger to PostHog as the free-text
// copy shown in the modal (`source_component: "Portfolio Analytics is a Pro
// feature — track aggregate…"`), so every copy edit split the funnel and a
// server-supplied message (the watchlist cap) could not be grouped at all.
// Every paywall now names a code from this list; the copy stays free.
//
// UPGRADE_TIER_FOR_REASON is the cheapest plan that actually removes the
// gate, so the prompt can sell Scout to someone blocked by a Scout-level
// gate instead of always saying "Unlock Pro" (the 2026-10-19 audit found
// the Leads tab, which Scout includes, opened a Pro-only modal).

export type UpgradeReasonCode =
  | "portfolio_tab"
  | "leads_tab"
  | "locked_property"
  | "watchlist_cap"
  | "county_subset_banner"
  | "county_not_in_plan"
  | "no_subscription"
  | "user_menu"
  | "export_bidders"
  | "bid_history"
  | "bid_intelligence"
  | "deal_analyzer"
  | "owner_contacts"
  | "unknown";

export type UpgradeTargetTier = "scout" | "pro";

export const UPGRADE_REASON_CODES: readonly UpgradeReasonCode[] = [
  "portfolio_tab",
  "leads_tab",
  "locked_property",
  "watchlist_cap",
  "county_subset_banner",
  "county_not_in_plan",
  "no_subscription",
  "user_menu",
  "export_bidders",
  "bid_history",
  "bid_intelligence",
  "deal_analyzer",
  "owner_contacts",
  "unknown",
];

/** Cheapest plan that removes the gate for a FREE viewer. A Scout viewer is
 *  always offered Pro (see targetTierForUpgrade). Mirrors the capability
 *  table in planCapabilities.ts: Scout has leads, full rows and owner data in
 *  its chosen counties, bid history, bid intelligence and the deal analyzer;
 *  only Pro has every county, unlimited watchlist and the buyer-list CSV. */
export const UPGRADE_TIER_FOR_REASON: Record<UpgradeReasonCode, UpgradeTargetTier> = {
  portfolio_tab: "scout",
  leads_tab: "scout",
  locked_property: "scout",
  watchlist_cap: "scout",
  county_subset_banner: "scout",
  county_not_in_plan: "pro",
  no_subscription: "scout",
  user_menu: "pro",
  export_bidders: "pro",
  bid_history: "scout",
  bid_intelligence: "scout",
  deal_analyzer: "scout",
  owner_contacts: "scout",
  unknown: "pro",
};

/** The tier an upgrade prompt should lead with for this viewer and gate. */
export function targetTierForUpgrade(viewerPlan: string | undefined, reason: UpgradeReasonCode): UpgradeTargetTier {
  if (viewerPlan === "scout") return "pro";
  return UPGRADE_TIER_FOR_REASON[reason] ?? "pro";
}

export function isUpgradeReasonCode(raw: string | null | undefined): raw is UpgradeReasonCode {
  return !!raw && (UPGRADE_REASON_CODES as readonly string[]).includes(raw);
}
